import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
  Box,
  Button,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import API from "../config/API";

const OverlayPanel = ({
  open,
  onClose,
  title,
  templates = [],
  onOptimize,
  setInput,
  buttonStyle,
  fetchTemplates,
  showSnackbar,
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [optimizerInput, setOptimizerInput] = useState("");
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [newTitle, setNewTitle] = useState("");

  const handleOptimize = async () => {
    if (!optimizerInput.trim()) return;

    try {
      const res = await API.post("/optimizer/prompt", {
        prompt: optimizerInput,
      });
      const optimized = res.data?.message || "";

      setOptimizerInput(optimized); // update textarea with result
      if (showSnackbar) showSnackbar("Prompt optimized");
    } catch (err) {
      console.error("❌ Optimization failed:", err);
      if (showSnackbar) showSnackbar("Optimization failed ❌");
    }
  };

  const handleEditTitle = async (id, title) => {
    try {
      await API.post("/templates/title", { id, title });
      showSnackbar("Template title updated.");
      setEditingTemplateId(null);
      fetchTemplates();
    } catch (err) {
      console.error("❌ Failed to update template title:", err);
      showSnackbar("Failed to update title.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.post("/templates/remove", { id });
      showSnackbar("Removed from templates.");
      fetchTemplates();
    } catch (err) {
      console.error("❌ Failed to remove:", err);
      showSnackbar("Failed to remove from templates.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        style: {
          borderRadius: 12,
          padding: 0,
        },
      }}
    >
      <DialogTitle sx={{ px: 3, pt: 2, pb: 1 }}>
        <span>&nbsp;</span>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={tabIndex}
        onChange={(_, val) => setTabIndex(val)}
        textColor="inherit"
        variant="fullWidth"
        TabIndicatorProps={{
          style: {
            backgroundColor: buttonStyle.backgroundColor,
            height: 3,
          },
        }}
        sx={{
          px: 3,
          backgroundColor: "#f9f9f9", // or transparent if you want
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Tab
          label="Templates"
          disableRipple
          sx={{
            fontWeight: 600,
            transition: "none",
            "&.Mui-selected": {
              color: "white",
              backgroundColor: buttonStyle.backgroundColor,
            },
            "&:hover": {
              backgroundColor: buttonStyle.backgroundColor,
              color: "white",
            },
            borderRadius: "10px",
          }}
        />
        <Tab
          label="Optimizer"
          disableRipple
          sx={{
            fontWeight: 600,
            transition: "none",
            "&.Mui-selected": {
              color: "white",
              backgroundColor: buttonStyle.backgroundColor,
            },
            "&:hover": {
              backgroundColor: buttonStyle.backgroundColor,
              color: "white",
            },
            borderRadius: "10px",
          }}
        />
      </Tabs>

      <DialogContent dividers sx={{ minHeight: "300px" }}>
        {tabIndex === 0 && (
          <Box>
            {templates.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No templates yet.
              </Typography>
            ) : (
              <Box component="ul" sx={{ pl: 2, listStyle: "none", m: 0 }}>
                {templates.map((tpl) => (
                  <Box
                    key={tpl._id}
                    component="li"
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      backgroundColor: "background.default",
                      transition: "0.2s",
                      position: "relative",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <Box
                      onClick={() => {
                        if (editingTemplateId) return;
                        if (setInput) setInput(tpl.prompt);
                        if (onClose) onClose();
                      }}
                      sx={{ cursor: editingTemplateId ? "default" : "pointer" }}
                    >
                      {editingTemplateId === tpl.message_id ? (
                        <Box display="flex" gap={1} alignItems="center">
                          <input
                            type="text"
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            style={{
                              flexGrow: 1,
                              fontSize: "0.95rem",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              border: "1px solid #ccc",
                            }}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() =>
                              handleEditTitle(tpl.message_id, newTitle)
                            }
                            sx={{
                              fontSize: "0.75rem",
                              backgroundColor: buttonStyle.backgroundColor,
                              "&:hover": {
                                backgroundColor: buttonStyle.backgroundColor,
                              },
                              "&:focus": {
                                backgroundColor: `${buttonStyle.backgroundColor}`,
                              },
                            }}
                          >
                            Save
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setEditingTemplateId(null)}
                            sx={{
                              fontSize: "0.75rem",
                              color: buttonStyle.backgroundColor,
                              "&:hover": {
                                backgroundColor: `${buttonStyle.backgroundColor}`,
                              },
                            }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      ) : (
                        <Typography variant="subtitle2" fontWeight={600}>
                          {tpl.title || "Untitled"}
                        </Typography>
                      )}

                      <Typography
                        sx={{
                          mt: 0.5,
                          color: "text.secondary",
                          whiteSpace: "pre-wrap",
                          fontSize: "0.9rem",
                        }}
                      >
                        {tpl.prompt.length > 120
                          ? tpl.prompt.slice(0, 120) + "..."
                          : tpl.prompt}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        display: "flex",
                        gap: 1,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOptimizerInput(tpl.prompt); // load prompt into optimizer input
                          setTabIndex(1); // switch to Optimizer tab
                        }}
                        sx={{
                          "&:hover": {
                            backgroundColor: `${buttonStyle.backgroundColor}`,
                          },
                        }}
                      >
                        {/* You can use a ✨ icon or import one */}
                        <span role="img" aria-label="Optimize">
                          ⚡
                        </span>
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingTemplateId(tpl.message_id);
                          setNewTitle(tpl.title || "");
                        }}
                        sx={{
                          "&:hover": {
                            backgroundColor: `${buttonStyle.backgroundColor}`,
                          },
                          "&:focus": {
                            backgroundColor: `${buttonStyle.backgroundColor}`,
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            window.confirm(
                              "Are you sure you want to delete this template?",
                            )
                          ) {
                            handleDelete(tpl.message_id);
                          }
                        }}
                        sx={{
                          "&:hover": {
                            backgroundColor: `${buttonStyle.backgroundColor}`, // translucent hover effect
                          },
                          "&:focus": {
                            backgroundColor: `${buttonStyle.backgroundColor}`,
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {tabIndex === 1 && (
          <Box display="flex" flexDirection="column" gap={2}>
            <textarea
              value={optimizerInput}
              onChange={(e) => setOptimizerInput(e.target.value)}
              placeholder="Enter a prompt to optimize..."
              style={{
                width: "100%",
                minHeight: "150px",
                padding: "10px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                fontFamily: "inherit",
              }}
            />
            <Box display="flex" justifyContent="space-between" gap={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleOptimize}
                disabled={!optimizerInput.trim()}
                sx={{
                  backgroundColor: buttonStyle.backgroundColor,
                  "&:hover": {
                    backgroundColor: buttonStyle.backgroundColor,
                  },
                  "&:focus": {
                    backgroundColor: buttonStyle.backgroundColor,
                  },
                }}
              >
                Optimize
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  if (setInput) setInput(optimizerInput);
                  setOptimizerInput("");
                  if (onClose) onClose();
                }}
                disabled={!optimizerInput.trim()}
                sx={{
                  backgroundColor: buttonStyle.backgroundColor,
                  "&:hover": {
                    backgroundColor: buttonStyle.backgroundColor,
                  },
                  "&:focus": {
                    backgroundColor: buttonStyle.backgroundColor,
                  },
                }}
              >
                Use
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OverlayPanel;
